import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import VStack from "@components/layout/VStack";
import TitlePage from "@components/layout/TitlePage";
import { defaultPagination, titlePages } from "@shared/constants/appConstants";
import { FilterTypeEnum, FindFavoritePageType, SortByValue } from "@shared/types/enums/globalEnum";
import FindAvailableTime from "@features/search/components/FindAvailableTime";
import ResourceCard from "@features/resources/components/ResourceCard";
import useScrollShadow from "@shared/hooks/useScrollShadow";
import { Loader } from "@components/ui/Loader";
import HStack from "@components/layout/HStack";
import ResourceTypeToolbar from "@components/ui/ResourceTypeToolbar";
import { useAppointmentStore, useOutlookStore, useResourceStore, useSettingStore, useThemeStore } from "@shared/stores";
import { DeskLocationEntity, ResourceEntity } from "@features/resources/services/types/resourceEntity";
import Menu from "@components/ui/Menu";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, RewardIcon, StarIcon } from "@/pages/taskpane/components/icons/IconCustom";
import { generateCId } from "@/pages/taskpane/lib/utils/randomUUID";
import { useFilterResources } from "../../search/services/useFilterResources";
import { useScrollLoadMore } from "@/pages/taskpane/shared/hooks";
import EmptyData from "@/pages/taskpane/components/ui/EmptyData";
import { QUERY_KEY } from "@/pages/taskpane/shared/constants/queryKey";
import { useQueryClient } from "@tanstack/react-query";

const FindFavorite = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const { selectedResource } = useResourceStore();
  const { defaultResourceType } = useSettingStore();
  const { startDateTimeUtc, endDateTimeUtc, timeZone } = useAppointmentStore();
  const { organizer, allAttendees, recurringDates, isNoEndDateRecurring, iCalUId, customUid, globalId } =
    useOutlookStore();
  const [type, setType] = useState<FindFavoritePageType>(FindFavoritePageType.Favorite);
  const [searchParams] = useSearchParams();
  const resourceType = useMemo(() => {
    return searchParams.get("resourceType") || defaultResourceType;
  }, [searchParams]);
  const { setDeskLocationList } = useResourceStore();

  const resourceListRef = useRef<HTMLDivElement | null>(null);

  const queryClient = useQueryClient();

  const titlePage = useMemo(() => {
    switch (type) {
      case FindFavoritePageType.MostUsed:
        return { title: t(titlePages.findMostUsed.title), description: t(titlePages.findMostUsed.description) };
      default:
        return { title: t(titlePages.findFavorite.title), description: t(titlePages.findFavorite.description) };
    }
  }, [type, t]);

  const favoriteOptions = useMemo(() => {
    return [
      {
        id: 1,
        name: "Favorite",
        icon: <StarIcon isDarkMode={isDarkMode} isOffHover />,
        isSelected: type === FindFavoritePageType.Favorite,
        action: () => setType(FindFavoritePageType.Favorite),
      },
      {
        id: 2,
        name: "Most used",
        icon: <RewardIcon isDarkMode={isDarkMode} />,
        isSelected: type === FindFavoritePageType.MostUsed,
        action: () => setType(FindFavoritePageType.MostUsed),
      },
    ];
  }, [type, isDarkMode]);

  const payload = useMemo(
    () => ({
      filterType: type === FindFavoritePageType.Favorite ? FilterTypeEnum.Favorite : FilterTypeEnum.MostUsed,
      pageSize: defaultPagination.pageSize,
      requestId: generateCId(),
      uid: customUid || "",
      globalId: globalId || "",
      organizer: organizer || "",
      resourceType: resourceType || "",
      locationIds: [],
      timeZoneStandardName: timeZone || "",
      attendees: [organizer, ...allAttendees.map((att) => att.Email)],
      occurrences:
        recurringDates?.length > 0
          ? recurringDates.map((date) => ({
              utcStartDate: date.startDate || "",
              utcEndDate: date.endDate || "",
            }))
          : [
              {
                utcStartDate: startDateTimeUtc || "",
                utcEndDate: endDateTimeUtc || "",
              },
            ],
      isNoEndDate: recurringDates?.length > 0 ? isNoEndDateRecurring : null,
      isRecurring: recurringDates?.length > 0,
      isOnlyAvailable: false,
      name: "",
      sortBy: {
        field: SortByValue.name,
        operator: SortByValue.operatorAsc,
      },
    }),
    [
      organizer,
      resourceType,
      recurringDates,
      startDateTimeUtc,
      endDateTimeUtc,
      timeZone,
      allAttendees,
      isNoEndDateRecurring,
      iCalUId,
      customUid,
      globalId,
      type,
    ]
  );

  const {
    data: resourcesDataInfinite,
    fetchNextPage,
    isFetching,
    isError,
  } = useFilterResources({
    payload,
  });

  // Extract desk locations separately
  const extractedDeskLocations = useMemo(() => {
    const allDeskLocations = resourcesDataInfinite?.pages?.flatMap((page) => page.deskLocations) || [];
    const deskLocationList: DeskLocationEntity[] = [];
    allDeskLocations.forEach((location) => {
      if (!deskLocationList.some((item) => item.locationId === location.locationId)) {
        deskLocationList.push(location);
      }
    });
    return deskLocationList;
  }, [resourcesDataInfinite]);

  // Update store when desk locations change
  useEffect(() => {
    setDeskLocationList(extractedDeskLocations);
  }, [extractedDeskLocations, setDeskLocationList]);

  const resourceList = useMemo(() => {
    const allResources = resourcesDataInfinite?.pages?.flatMap((page) => page.resources) || [];
    return allResources.filter((r: ResourceEntity) => !selectedResource.some((c) => c.id === r.id));
  }, [resourcesDataInfinite, selectedResource]);

  const handleRemoveResourceFromFavorite = (resource: ResourceEntity) => {
    queryClient.setQueriesData(
      { queryKey: [QUERY_KEY.GET_RESOURCES, QUERY_KEY.FILTER_RESOURCES_FAVORITE] },
      (oldData: any) => {
        if (!oldData) {
          return oldData;
        }
        const newData = {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            resources: page.resources.filter((r: ResourceEntity) => r.id !== resource.id),
          })),
        };
        return newData;
      }
    );
  };

  // Load More Resources

  useScrollLoadMore({
    containerRef: resourceListRef,
    hasMore: !isError && !resourcesDataInfinite?.pages[resourcesDataInfinite?.pages?.length - 1]?.isLastPage,
    isLoading: isFetching,
    onLoadMore: () => {
      fetchNextPage();
    },
    threshold: 200,
  });

  useScrollShadow(resourceListRef);

  return (
    <VStack className="h-full px-4 pt-1 pb-4" justifyContent="between" gap={3}>
      {/* Title */}
      <HStack>
        <TitlePage
          title={titlePage?.title}
          description={titlePage?.description}
          menu={
            <Menu
              width="150px"
              position="left"
              options={favoriteOptions}
              icon={<ChevronDown isDarkMode={isDarkMode} />}
            />
          }
        />
      </HStack>

      <HStack className="w-full" justifyContent="between" gap={2}>
        <ResourceTypeToolbar />
      </HStack>

      <VStack ref={resourceListRef} className={`flex-1 overflow-y-auto no-scrollbar`} gap={2}>
        {resourceList?.length ? (
          resourceList.map((resource: ResourceEntity) => {
            return (
              <ResourceCard
                key={resource.id}
                resource={resource}
                onRemoveFromFavorite={handleRemoveResourceFromFavorite}
              />
            );
          })
        ) : !isFetching ? (
          <EmptyData content={t("No Resources available")} />
        ) : null}

        {isFetching ? (
          <HStack className="w-full justify-center p-4">
            <Loader texts={[t("Loading...")]} />
          </HStack>
        ) : null}
      </VStack>
      <FindAvailableTime />
    </VStack>
  );
};

export default FindFavorite;
